import React from 'react';
import { ScrollView, View, Platform, Text, Image } from 'react-native';
import moment from 'moment-timezone';
import { Button } from 'react-native-elements';
import { Icon as RNIcon } from 'react-native-elements';
import { CommonActions, useFocusEffect, useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import ContentHeader from 'components/ContentHeader';
import OrderDetailList from 'components/OrderDetailList';
import OrderResponseModal from 'components/OrderResponseModal';
import useChangeOrder from 'hooks/useChangeOrder';
import useLocalOrders from 'hooks/useLocalOrders';
import useTimeout from 'hooks/useTimeout';
import OrderDetailScreenProps from 'types/screens/OrderDetailScreenProps';
import AppContext from 'contexts/AppContext';
import { greenColor } from 'styles/global';
import { printTicket } from 'utils/setupTicket';
import OrderContext from 'contexts/OrderContext';
import { getOrder, translateOrderDescription } from 'services/ordersService';
import ShoppingCart from 'types/models/ShoppingCart';
import OrderStatuses from 'types/enums/OrderStatuses';
import Order from 'types/models/Order';
import { styles } from 'styles/screens/orderDetailScreen';
import { formatOrderId, getcolorStatusDescription } from 'utils/orders';
import OrderDescription from 'types/models/OrderDescription';
import OrderImageType from 'types/enums/OrderImageType';

export default ({ navigation, route }: OrderDetailScreenProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    selectedOrder,
    modalizeData,
    newlyCreatedOrder,
    currentVenue,
    redirectionItems,
    cloverConnected,
    pairedWithClover,
    setOrderModalResponseData,
  } = React.useContext(AppContext);
  const { setCurrentShoppingCart, setCurrentOrder } = React.useContext(OrderContext);
  const { isLoading, setIsLoading } = React.useContext(AppContext);
  const [orderWithDetails, setOrderWithDetails] = React.useState<Order | null>(null);
  const { deleteCurrentOrder, storeCurrentOrder } = useChangeOrder();
  // Evita repetir la impresión y la limpieza del carrito cuando el polling
  // vuelve a ejecutar el effect para la misma orden.
  const modalActionsOrderIdRef = React.useRef<number | null>(null);
  const { updateLocalOrderStatus } = useLocalOrders();
  const redirectNavigator = useNavigation();
  cloverConnected?.showMessage(`Muchas gracias por su compra, su Nro de orden es ${selectedOrder?.id}.`);
  const params = route.params;
  const [showModal, setShowModal] = React.useState<boolean>(params?.showModal ?? false);
  const qrImage = (orderWithDetails?.images ?? []).some((img) => img.orderDetailId)
    ? null
    : orderWithDetails?.images?.find((img) => img.orderImageTypeId === OrderImageType.Qr)?.path;

  const orderDescriptions: OrderDescription = {
    orderCreated: t('pending-payment'),
    orderPaymentAccepted: t('payment-accepted'),
    orderPreparing: t('preparing-order'),
    orderToDeliver: t('order-ready-to-deliver'),
    orderDelivering: t('delivering-order'),
    orderDelivered: t('order-delivered'),
    orderIncompleted: t('order-incompleted'),
    orderCanceled: t('order-canceled'),
    orderAddedToCheckingAccount: t('order-added-to-checking-account'),
  };

  const orderIsPaid = (order: Order) => {
    const invalidStatuses = [OrderStatuses.created, OrderStatuses.incompleted, OrderStatuses.canceled];
    return invalidStatuses.includes(order.orderStatusId);
  };

  const onBackToOrders = () => {
    deleteCurrentOrder();
    navigation.navigate('OrdersScreen');
  };

  const redirectToShoppingCart = (venueCode: string, commerceCode: string) => {
    navigation.dispatch({
      ...CommonActions.reset({
        index: 0,
        routes: [
          {
            name: 'Home',
            state: {
              routes: [
                {
                  name: 'ShoppingCartScreen',
                  params:
                    Platform.OS === 'web'
                      ? {
                          venueCode,
                          commerceCode,
                        }
                      : undefined,
                },
              ],
            },
          },
        ],
      }),
    });
  };

  const redirectToHome = () => {
    navigation.dispatch({
      ...CommonActions.reset({
        index: 0,
        routes: [
          {
            name: 'Home',
            state: {
              routes: [
                {
                  name: 'HomeScreen',
                },
              ],
            },
          },
        ],
      }),
    });
  };

  // Solo leemos la orden: el estado real (1 = created, 2 = paymentAccepted, etc.)
  // lo actualiza el backend cuando First Data o Sitef confirman el pago.
  const fetchOrderDetails = async () => {
    if (!selectedOrder) return;
    const order = await getOrder(selectedOrder.id);
    if (order) {
      setOrderWithDetails(order);
    }
  };

  useFocusEffect(
    React.useCallback(() => {
      setIsLoading(true);
      fetchOrderDetails();
      setIsLoading(false);

      return () => {
        const resetParams = () => {
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [{ name: 'OrdersScreen' }],
            }),
          );
        };
        resetParams();
      };
    }, []),
  );

  React.useEffect(() => {
    // Mientras el pago con tarjeta sigue en "created" (1) consultamos cada 5 s
    // para detectar el cambio a "paymentAccepted" (2) apenas lo procesa el backend.
    // Cuando el estado ya se resolvió, o si es efectivo, alcanza con 30 s.
    const isAwaitingPaymentResult =
      newlyCreatedOrder &&
      !selectedOrder?.isCash &&
      (!orderWithDetails || orderWithDetails.orderStatusId === OrderStatuses.created);
    const refreshInterval = isAwaitingPaymentResult ? 5000 : 30000;
    const orderDetailInterval = setInterval(() => {
      fetchOrderDetails();
    }, refreshInterval);
    return () => {
      clearInterval(orderDetailInterval);
    };
  }, [newlyCreatedOrder, selectedOrder?.id, selectedOrder?.isCash, orderWithDetails?.orderStatusId]);

  useFocusEffect(
    React.useCallback(() => {
      if (!selectedOrder) {
        setTimeout(() => {
          redirectToHome();
        }, 500);
        return;
      }
      if (newlyCreatedOrder && showModal === true) {
        // Puede venir de la orden recién creada (todavía en 1) o del detalle ya
        // consultado. El modal solo debe mostrar éxito con el estado real del backend.
        const orderStatusId = orderWithDetails?.orderStatusId ?? selectedOrder.orderStatusId;
        const isPaymentPending = orderStatusId === OrderStatuses.created && !selectedOrder.isCash;
        // 7 = incompleted y 8 = canceled: el pago no se completó.
        // Para efectivo no aplicamos esta lógica porque el cobro se resuelve fuera de la app.
        const hasPaymentFailed =
          !selectedOrder.isCash && [OrderStatuses.incompleted, OrderStatuses.canceled].includes(orderStatusId);

        setOrderModalResponseData({
          content: (
            <OrderResponseModal
              message={hasPaymentFailed ? '' : selectedOrder.delivery ?? ''}
              success={!hasPaymentFailed}
              isPaymentPending={isPaymentPending}
              orderNumber={selectedOrder.id}
              orderStatusId={orderStatusId}
              orderImage={
                selectedOrder?.images?.some((img) => img.orderDetailId)
                  ? undefined
                  : selectedOrder?.images?.find((img) => img.orderImageTypeId !== OrderImageType.ContactQr)
              }
              verificationCode={selectedOrder.verificationCode}
              onClose={() => {
                setShowModal(false);
                setOrderModalResponseData(null);
              }}
              navigation={navigation}
            />
          ),
        });

        if (modalActionsOrderIdRef.current !== selectedOrder.id) {
          modalActionsOrderIdRef.current = selectedOrder.id;

          if (pairedWithClover && Platform.OS === 'web') {
            printTicket({
              data: selectedOrder,
              orderNum: selectedOrder.id,
            });
          }

          deleteCurrentOrder();
        }
      }
    }, [selectedOrder, newlyCreatedOrder, showModal, orderWithDetails]),
  );

  if (newlyCreatedOrder) {
    useTimeout(() => {
      // La redirección al home automáticamente se hace solo cuando está conectado a un clover
      if (pairedWithClover && Platform.OS === 'web') {
        redirectNavigator.reset({
          index: 0,
          routes: [
            {
              name: 'Home',
              state: {
                routes: [
                  {
                    name: 'CommerceScreen',
                    params: redirectionItems ?? undefined,
                  },
                ],
              },
            },
          ],
        });
        setOrderModalResponseData(null);
      }
    }, 5000);
  }

  React.useEffect(() => {
    const saveOrders = async () => {
      if (!orderWithDetails) return;
      updateLocalOrderStatus(orderWithDetails.orderStatusId, orderWithDetails);
    };
    saveOrders();
  }, [orderWithDetails]);

  const onContinuePayment = async () => {
    if (!selectedOrder || !orderWithDetails || !orderWithDetails.details) return;
    const shoppingCart: ShoppingCart = {
      commerce: {
        id: orderWithDetails.commerce.id,
        code: orderWithDetails.commerce.code,
        name: orderWithDetails.commerce.name,
      },
      detail: orderWithDetails.details,
      paid: false,
      processed: true,
      phone: orderWithDetails.phone,
      isCash: orderWithDetails.isCash,
      guestEmail: orderWithDetails.guestEmail ?? '',
      delivery: orderWithDetails.delivery ?? '',
      identification: orderWithDetails.identification ?? 0,
    };
    const newOrder: Order = {
      id: orderWithDetails.id,
      totalAmount: orderWithDetails.totalAmount,
      verificationCode: orderWithDetails.verificationCode,
      orderStatusId: orderWithDetails.orderStatusId,
      commerce: orderWithDetails.commerce,
      phone: orderWithDetails.phone,
      identification: orderWithDetails.identification,
      isCash: orderWithDetails.isCash,
      isDiscount: orderWithDetails.isDiscount,
      createdAt: orderWithDetails.createdAt,
      updatedAt: orderWithDetails.updatedAt,
      localDateTime: orderWithDetails.localDateTime,
      buyer: orderWithDetails.buyer ?? '',
      delivery: orderWithDetails.delivery ?? '',
      guestEmail: orderWithDetails.guestEmail ?? '',
      details: orderWithDetails.details,
      venue: selectedOrder.venue,
      isCourtesy: selectedOrder.isCourtesy,
    };
    setCurrentShoppingCart(shoppingCart);
    setCurrentOrder(newOrder);
    await storeCurrentOrder(newOrder);
    redirectToShoppingCart(newOrder?.venue?.code ?? '', shoppingCart.commerce.code);
  };

  const getPaymentMethod = (): string => {
    const order = orderWithDetails;
    if (!order) return '';

    if (order.payment?.isBalance) {
      return t('payments.balance');
    } else if (order.isCash) {
      return t('payments.cash');
    } else if (order.isCourtesy) {
      return t('payments.courtesy');
    } else if (order.payment?.fiservQrPayment) {
      return t('payments.qr');
    } else if (
      order.payment?.cloverPointPayment &&
      ['Mercado Pago', 'Pix'].includes(order.payment?.cloverPointPayment?.paymentMethod)
    ) {
      return order.payment?.cloverPointPayment?.paymentMethod;
    } else {
      return t('payments.card');
    }
  };

  const renderContent = (): JSX.Element => {
    if (isLoading || !orderWithDetails || !orderWithDetails.details) return <></>;
    const { commerce } = orderWithDetails;
    return (
      <View>
        <View style={styles.orderStatusContainer}>
          <RNIcon name={'check-circle'} color={getcolorStatusDescription(orderWithDetails.orderStatusId)} size={65} />
          <Text
            style={[styles.statusDescriptionText, { color: getcolorStatusDescription(orderWithDetails.orderStatusId) }]}
          >
            {translateOrderDescription(orderWithDetails.orderStatusId, orderDescriptions)}
          </Text>
        </View>
        <View style={styles.detailsContainer}>
          <Text style={styles.commerceNameText}>{commerce.name}</Text>
          <OrderDetailList orderWithDetails={orderWithDetails} detail={orderWithDetails.details} />
          <View style={styles.separator} />
          <View>
            <Text
              style={{
                fontStyle: 'normal',
                fontWeight: '500',
                fontSize: 16,
                lineHeight: 20,
              }}
            >
              {t('order-detail')}
            </Text>
            <View style={{ padding: 10 }}>
              <View style={{ display: 'flex', flexDirection: 'row', marginTop: 10 }}>
                <Text style={styles.detailPaymentText}>{t('done')}</Text>
                <Text style={styles.detailPaymentText}>
                  {moment(orderWithDetails.localDateTime).format('DD/MM/YY - HH:mm')}hs
                </Text>
              </View>
              {orderWithDetails.payment ? (
                <>
                  <View style={{ display: 'flex', flexDirection: 'row', marginTop: 10 }}>
                    <Text style={styles.detailPaymentText}>{t('paid')}</Text>
                    <Text style={styles.detailPaymentText}>
                      {moment(orderWithDetails.payment.createdAt).format('DD/MM/YY - HH:mm')}hs
                    </Text>
                  </View>

                  <View style={{ display: 'flex', flexDirection: 'row', marginTop: 10 }}>
                    <Text style={styles.detailPaymentText}>{t('payment-method')}</Text>
                    <Text style={styles.detailPaymentText}>{getPaymentMethod()}</Text>
                  </View>
                </>
              ) : null}
            </View>
          </View>
          {qrImage && orderIsPaid(orderWithDetails) ? (
            <View>
              <Text
                style={{
                  fontStyle: 'normal',
                  fontWeight: '500',
                  fontSize: 16,
                  lineHeight: 20,
                }}
              >
                QR
              </Text>
              <View style={{ padding: 10 }}>
                <View style={{ marginTop: 10 }}>
                  <Image
                    source={{ uri: qrImage }}
                    style={{
                      width: 120,
                      height: 120,
                    }}
                  />
                </View>
              </View>
            </View>
          ) : null}
        </View>
      </View>
    );
  };

  const orderIsCash = orderWithDetails && orderWithDetails.isCash;
  const orderIsDiscount = orderWithDetails && orderWithDetails.isDiscount;

  return (
    <View style={styles.container}>
      <ScrollView scrollEnabled={!modalizeData}>
        <ContentHeader
          title={`${t('order-detail-title')} ${
            orderWithDetails ? formatOrderId(orderWithDetails.id, orderWithDetails.orderStatusId) : ''
          }`}
          hasOrders={true}
          onPreviousScreen={onBackToOrders}
        />
        {renderContent()}
      </ScrollView>
      {!newlyCreatedOrder &&
        !orderIsCash &&
        !orderIsDiscount &&
        orderWithDetails &&
        orderWithDetails.orderStatusId === OrderStatuses.created && (
          <Button
            title={t('finalize-purchase')}
            type="clear"
            containerStyle={[styles.confirmPaymentBtnContainer]}
            buttonStyle={[styles.confirmPaymentBtn, { backgroundColor: currentVenue?.appColor ?? greenColor }]}
            titleStyle={styles.confirmPaymentBtnText}
            onPress={() => {
              onContinuePayment();
            }}
          />
        )}
    </View>
  );
};
